import axios from 'axios';
import { useState } from 'react';

const MainSearch = () => {
  const [movieResults, setMovieResults] = useState([]);
  const [tvResults, setTvResults] = useState([]);
  const [gameResults, setGameResults] = useState([]);
  const [productResults, setProductResults] = useState([]);
  const [userSearchText, setUserSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  const tmdbApiKey = process.env.REACT_APP_TMDB_API_KEY;
  const videoGamesApiKey = process.env.REACT_APP_RAWGIO_API_KEY;
  const server_url = process.env.REACT_APP_API_URI;
  const videoGameOrderingCriteria = '-playtime,-added';

  const handleUserSearchTextChange = (e) => {
    setUserSearchText(e.target.value.trim());
  };

  const handleSearch = async () => {
    if (userSearchText.length === 0) {
      setShowDropdown(false);
      return;
    }

    setShowDropdown(true);
    setLoading(true);

    try {
      const [gamesRes, moviesRes, tvRes, productsRes] =
        await Promise.allSettled([
          axios.get(
            `https://api.rawg.io/api/games?key=${videoGamesApiKey}&ordering=${videoGameOrderingCriteria}&search=${userSearchText}&page_size=5`,
          ),
          axios.get(
            `https://api.themoviedb.org/3/search/movie?api_key=${tmdbApiKey}&query=${userSearchText}&include_adult=false&language=en-US&page=1`,
          ),
          axios.get(
            `https://api.themoviedb.org/3/search/tv?api_key=${tmdbApiKey}&query=${userSearchText}&include_adult=false&language=en-US&page=1`,
          ),
          axios.get(
            `${server_url}/my-products/search-products/${userSearchText}`,
          ),
        ]);

      setGameResults(
        gamesRes.status === 'fulfilled'
          ? gamesRes.value.data.results || []
          : [],
      );
      setMovieResults(
        moviesRes.status === 'fulfilled'
          ? moviesRes.value.data.results || []
          : [],
      );
      setTvResults(
        tvRes.status === 'fulfilled' ? tvRes.value.data.results || [] : [],
      );
      setProductResults(
        productsRes.status === 'fulfilled'
          ? productsRes.value.data.data || []
          : [],
      );
    } catch (error) {
      console.error('Search API Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderMovie = (title, results, keyName, className) =>
    results.length > 0 && (
      <div className={className}>
        <h3 className="text-lg font-bold mb-4 p-4 rounded-lg text-center mt-4 bg-primary text-secondary">
          {title}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {results.map((item) => (
            <div
              key={item.id}
              className="p-4 border rounded-xl shadow-md bg-white cursor-pointer hover:bg-gray-50 transition-transform transform hover:scale-105"
              onClick={() => (window.location.href = `/rate/game/${item.id}`)}
            >
              <div className="w-full min-h-36 overflow-hidden rounded-lg mb-4">
                <img
                  src={`https://image.tmdb.org/t/p/original/${item.backdrop_path}`}
                  className="w-full h-full object-cover"
                  alt={item.name || 'Game Image'}
                />
              </div>
              <h4 className="text-xl font-semibold mb-1">{item.title}</h4>
              <p className="text-gray-500">Movie</p>
              <p className="text-gray-400">
                Released: {item.release_date?.substring(0, 4) || '-'}
              </p>
            </div>
          ))}
        </div>
      </div>
    );

  const renderShows = (title, results, keyName, className) =>
    results.length > 0 && (
      <div className={className}>
        <h3 className="text-lg font-bold mb-4 p-4 rounded-lg text-center mt-4 bg-primary text-secondary">
          {title}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {results.map((item) => (
            <div
              key={item.id}
              className="p-4 border rounded-xl shadow-md bg-white cursor-pointer hover:bg-gray-50 transition-transform transform hover:scale-105"
              onClick={() => (window.location.href = `/rate/game/${item.id}`)}
            >
              <div className="w-full min-h-36 overflow-hidden rounded-lg mb-4">
                <img
                  src={`https://image.tmdb.org/t/p/original/${item.backdrop_path}`}
                  className="w-full h-full object-cover"
                  alt={item.name || 'Game Image'}
                />
              </div>
              <h4 className="text-xl font-semibold mb-1">{item.name}</h4>
              <p className="text-gray-500">TV Show</p>
              <p className="text-gray-400">
                Released: {item.release_date?.substring(0, 4) || '-'}
              </p>
            </div>
          ))}
        </div>
      </div>
    );

  const renderGames = (title, results, keyName, className) =>
    results.length > 0 && (
      <div className={className}>
        <h3 className="text-lg font-bold mb-4 p-4 rounded-lg text-center mt-4 bg-primary text-secondary">
          {title}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {results.map((item) => (
            <div
              key={item.id}
              className="p-4 border rounded-xl shadow-md bg-white cursor-pointer hover:bg-gray-50 transition-transform transform hover:scale-105"
              onClick={() => (window.location.href = `/rate/game/${item.id}`)}
            >
              <div className="w-full min-h-36 overflow-hidden rounded-lg mb-4">
                <img
                  src={item.background_image || '/placeholder.jpg'}
                  className="w-full h-full object-cover"
                  alt={item.name || 'Game Image'}
                />
              </div>
              <h4 className="text-xl font-semibold mb-1">{item.name}</h4>
              <p className="text-gray-500">Video Game</p>
              <p className="text-gray-400">
                Released: {item.release_date?.substring(0, 4) || '-'}
              </p>
            </div>
          ))}
        </div>
      </div>
    );

  const renderProduct = (title, results, keyName, className) =>
    results.length > 0 && (
      <div className={className}>
        <h3 className="text-lg font-bold mb-2 p-3 rounded-lg text-center mt-4 bg-primary text-secondary">
          {title}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {results.map((item) => (
            <div
              key={item.id}
              className="p-4 border rounded-lg shadow-lg bg-white cursor-pointer hover:bg-gray-100"
              onClick={() =>
                (window.location.href = `/rate/${item.categoryName.toLowerCase()}/${item.contentId}`)
              }
            >
              <b>{item.contentName}</b> <br />
              Category: {item.categoryName}
            </div>
          ))}
        </div>
      </div>
    );

  return (
    <div className="relative w-full max-w-6xl mx-auto px-4 min-h-[calc(100vh-300px)]">
      <h1 className="text-2xl font-bold mt-4 text-secondary capitalize">
        Search
      </h1>

      <div className="flex items-center gap-2 w-full max-w-lg mx-auto my-4">
        <input
          type="search"
          className="flex-1 h-12 px-4 text-lg text-gray-800 bg-white rounded-full border border-gray-300 focus:outline-none focus:ring-0 shadow-sm"
          placeholder="Search movies, games, products..."
          value={userSearchText}
          onChange={handleUserSearchTextChange}
          onFocus={() => setShowDropdown(true)}
        />
        <button
          onClick={handleSearch}
          className="h-12 px-6 bg-secondary font-semibold text-primary rounded-full shadow-lg hover:bg-primary-dark"
        >
          Search
        </button>
      </div>

      {showDropdown && (
        <div className="relative w-full mt-2 mb-5 bg-white border border-gray-300 rounded-xl shadow-lg p-4 max-h-[500px] overflow-y-auto">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <>
              {/* <h2 className="text-lg font-bold mb-4">Search Results:</h2> */}
              {renderMovie('Movies', movieResults, 'title', 'movies-section')}
              {renderShows('TV Shows', tvResults, 'name', 'tv-section')}
              {renderGames('Games', gameResults, 'name', 'games-section')}
              {renderProduct(
                'Products',
                productResults,
                'name',
                'products-section',
              )}
              {movieResults.length === 0 &&
                tvResults.length === 0 &&
                gameResults.length === 0 &&
                productResults.length === 0 && <p>No results found.</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MainSearch;
